import { z } from 'zod'
import { storageUrlsSchema } from './interfaces/storage.js'

export const mediaReferenceTypes = ['audio', 'video'] as const

/**
 * `.strict()`, and a strict `storageUrls`: the projection must never carry entity fields such as `acls` or
 * `userId`, nor the signed feature URLs `decorateStorageUrls` adds (spec §4.1). `duration` and `peaksUrl` are the
 * only feature data it carries.
 */
export const availableMediaReferenceSchema = z
  .object({
    status: z.literal('available'),
    id: z.string(),
    type: z.enum(mediaReferenceTypes),
    name: z.string().describe('Display name of the media').optional(),
    parentId: z.string().describe("Media's parent id — lets the owner resolve a parent song as linked song").optional(),
    duration: z.number().describe('Stored length in seconds (audio features.base.duration)').optional(),
    peaksUrl: z.string().describe('Signed (cloud) or same-origin (local) URL of the waveform peaks JSON').optional(),
    storageUrls: storageUrlsSchema.strict(),
  })
  .strict()

/** Only the id: no name and no reason, so the endpoint cannot be used to probe other ids */
export const unavailableMediaReferenceSchema = z
  .object({
    status: z.literal('unavailable'),
    id: z.string(),
  })
  .strict()

export const mediaReferenceSchema = z.discriminatedUnion('status', [
  availableMediaReferenceSchema,
  unavailableMediaReferenceSchema,
])

export const entityMediaResponseSchema = z.object({ items: z.array(mediaReferenceSchema) })

export type AvailableMediaReference = z.infer<typeof availableMediaReferenceSchema>
export type UnavailableMediaReference = z.infer<typeof unavailableMediaReferenceSchema>
export type MediaReference = z.infer<typeof mediaReferenceSchema>
export type EntityMediaResponse = z.infer<typeof entityMediaResponseSchema>

export function isAvailableMediaReference(ref: MediaReference): ref is AvailableMediaReference {
  return ref.status === 'available'
}
